import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import styles from './OpenAINotesTool.module.css';

const DEFAULT_MODEL = 'gpt-5-nano';
const MODE_UPLOAD = 'upload';
const MODE_BATCH = 'batch';

function OpenAINotesTool() {
  const [selectedMode, setSelectedMode] = useState(MODE_UPLOAD);
  const [selectedFile, setSelectedFile] = useState(null);
  const [instructions, setInstructions] = useState('');
  const [model, setModel] = useState(DEFAULT_MODEL);
  const [result, setResult] = useState(null);
  const [batchResult, setBatchResult] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [volumes, setVolumes] = useState([]);
  const [volumesLoading, setVolumesLoading] = useState(false);
  const [volumesError, setVolumesError] = useState('');
  const [selectedVolumeId, setSelectedVolumeId] = useState('');
  const [selectedChapterIds, setSelectedChapterIds] = useState([]);

  const backendBaseUrl = useMemo(() => {
    const baseUrl = api.defaults.baseURL || '';
    if (!baseUrl) {
      return '';
    }
    const trimmed = baseUrl.replace(/\/?$/, '');
    return trimmed.replace(/\/api$/, '');
  }, []);

  const docsUrl = backendBaseUrl
    ? `${backendBaseUrl}/docs#/ai-notes/generate_ai_notes_ai_notes__post`
    : '/docs#/ai-notes/generate_ai_notes_ai_notes__post';

  const resolveStaticUrl = useCallback(
    (url) => {
      if (!url) {
        return '';
      }
      if (/^https?:\/\//i.test(url)) {
        return url;
      }
      return backendBaseUrl ? `${backendBaseUrl}${url}` : url;
    },
    [backendBaseUrl],
  );

  const resetState = useCallback(() => {
    setError('');
    setResult(null);
    setBatchResult(null);
  }, []);

  const handleModeChange = useCallback(
    (mode) => {
      if (mode === selectedMode) {
        return;
      }
      setSelectedMode(mode);
      setSelectedFile(null);
      setSelectedChapterIds([]);
      resetState();
    },
    [selectedMode, resetState],
  );

  const loadVolumes = useCallback(async () => {
    setVolumesLoading(true);
    setVolumesError('');
    try {
      const { data } = await api.get('/volumes/', { params: { limit: 500 } });
      const list = Array.isArray(data) ? data : [];
      setVolumes(list);
      setSelectedVolumeId((prev) => {
        if (prev && list.some((item) => String(item.id) === String(prev))) {
          return prev;
        }
        return list.length > 0 ? String(list[0].id) : '';
      });
    } catch (err) {
      const message =
        err?.response?.data?.detail || err?.message || '获取卷列表失败，请稍后重试';
      setVolumesError(message);
    } finally {
      setVolumesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedMode !== MODE_BATCH) {
      return;
    }
    if (volumes.length === 0 && !volumesLoading) {
      loadVolumes();
    }
  }, [selectedMode, volumes.length, volumesLoading, loadVolumes]);

  useEffect(() => {
    if (selectedMode === MODE_BATCH) {
      setSelectedChapterIds([]);
    }
  }, [selectedMode, selectedVolumeId]);

  const chaptersInVolume = useMemo(() => {
    if (!selectedVolumeId) {
      return [];
    }
    const volume = volumes.find((item) => String(item.id) === String(selectedVolumeId));
    if (!volume || !Array.isArray(volume.chapters)) {
      return [];
    }
    return [...volume.chapters].sort((a, b) => {
      const left = typeof a.order_index === 'number' ? a.order_index : 0;
      const right = typeof b.order_index === 'number' ? b.order_index : 0;
      return left - right;
    });
  }, [selectedVolumeId, volumes]);

  const allChaptersSelected = useMemo(() => {
    return (
      chaptersInVolume.length > 0 && selectedChapterIds.length === chaptersInVolume.length
    );
  }, [chaptersInVolume, selectedChapterIds.length]);

  const notesReadyCount = useMemo(() => {
    return chaptersInVolume.filter((item) => Boolean(item.has_ai_notes)).length;
  }, [chaptersInVolume]);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] ?? null;
    event.target.value = '';

    if (file && !/\.json$/i.test(file.name)) {
      setSelectedFile(null);
      setError('文件格式错误，请上传 JSON 文件');
      resetState();
      return;
    }

    setSelectedFile(file);
    setError('');
    setResult(null);
    setBatchResult(null);
  };

  const handleChapterToggle = (chapterId) => {
    setSelectedChapterIds((prev) => {
      const id = String(chapterId);
      if (prev.includes(id)) {
        return prev.filter((item) => item !== id);
      }
      return [...prev, id];
    });
  };

  const handleSelectAllChapters = () => {
    if (allChaptersSelected) {
      setSelectedChapterIds([]);
    } else {
      setSelectedChapterIds(chaptersInVolume.map((item) => String(item.id)));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setResult(null);
    setBatchResult(null);

    if (selectedMode === MODE_UPLOAD) {
      if (!selectedFile) {
        setError('请先选择要上传的 JSON 文件');
        return;
      }

      if (selectedFile.size === 0) {
        setError('文件为空，请重新上传');
        return;
      }

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('model', model || DEFAULT_MODEL);
      if (instructions.trim()) {
        formData.append('instructions', instructions.trim());
      }

      setIsGenerating(true);
      try {
        const { data } = await api.post('/ai-notes/', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 0,
        });
        setResult(data);
      } catch (err) {
        console.error('生成 AI 笔记失败:', err);
        const detail = err?.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : '生成失败，请稍后重试');
      } finally {
        setIsGenerating(false);
      }
      return;
    }

    if (selectedChapterIds.length === 0) {
      setError('请选择至少一个章节');
      return;
    }

    const payload = {
      chapter_ids: selectedChapterIds.map((id) => Number(id)),
      model: model.trim() || DEFAULT_MODEL,
    };
    if (instructions.trim()) {
      payload.instructions = instructions.trim();
    }

    setIsGenerating(true);
    try {
      const { data } = await api.post('/ai-notes/batch', payload, {
        timeout: 0,
      });
      setBatchResult(data);
    } catch (err) {
      const detail =
        err?.response?.data?.detail || err?.message || '批量生成失败，请稍后重试';
      setError(detail);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setInstructions('');
    setModel(DEFAULT_MODEL);
    setResult(null);
    setBatchResult(null);
    setError('');
    if (selectedMode === MODE_BATCH) {
      setSelectedChapterIds([]);
    }
  };

  return (
    <div className={styles.notesTool}>
      <section className={styles.sectionCard}>
        <div className={styles.sectionHeader}>
          <div className={styles.modeSwitch}>
            <button
              type="button"
              className={`${styles.modeButton} ${
                selectedMode === MODE_UPLOAD ? styles.modeButtonActive : ''
              }`}
              onClick={() => handleModeChange(MODE_UPLOAD)}
            >
              单章上传
            </button>
            <button
              type="button"
              className={`${styles.modeButton} ${
                selectedMode === MODE_BATCH ? styles.modeButtonActive : ''
              }`}
              onClick={() => handleModeChange(MODE_BATCH)}
            >
              批量生成
            </button>
          </div>
          <h2 className={styles.sectionTitle}>OpenAI 笔记生成</h2>
          <p className={styles.sectionHint}>
            {selectedMode === MODE_UPLOAD
              ? '上传章节的 JSON 句子文件，系统按批次调用 OpenAI 生成结构化笔记，并返回下载链接。'
              : '选择卷和章节，后台会读取章节的 content_trans 字段，多线程调用 OpenAI 生成笔记，并写入章节的 ai_notes 字段。'}
          </p>
        </div>

        <form className={styles.formGrid} onSubmit={handleSubmit}>
          {selectedMode === MODE_UPLOAD ? (
            <>
              <div className={styles.fieldGroup}>
                <label className={styles.label}>选择章节 JSON 文件 *</label>
                <div className={styles.fileInput}>
                  <input
                    type="file"
                    accept=".json,application/json"
                    onChange={handleFileChange}
                    className={styles.input}
                    disabled={isGenerating}
                  />
                  {selectedFile && (
                    <span className={styles.fileName}>
                      当前文件：{selectedFile.name}（{(selectedFile.size / 1024).toFixed(1)} KB）
                    </span>
                  )}
                </div>
                <p className={styles.description}>
                  文件需包含 chapters API 导出的 sentences 数组；服务会自动补全 sentence_id 并生成 key_phrases。
                </p>
              </div>
            </>
          ) : (
            <>
              <div className={styles.fieldGroup}>
                <label className={styles.label}>选择卷 *</label>
                <select
                  value={selectedVolumeId}
                  onChange={(event) => setSelectedVolumeId(event.target.value)}
                  className={styles.select}
                  disabled={volumesLoading || isGenerating || volumes.length === 0}
                >
                  {volumes.map((volume) => (
                    <option key={volume.id} value={volume.id}>
                      #{volume.id} · {volume.title}
                      {volume.novel ? `（${volume.novel.title}）` : ''}
                    </option>
                  ))}
                </select>
                {volumesError && <div className={styles.errorMessage}>{volumesError}</div>}
                {!volumesLoading && volumes.length === 0 && !volumesError && (
                  <div className={styles.statusMessage}>暂无卷数据，请先导入章节。</div>
                )}
              </div>

              <div className={styles.fieldGroup}>
                <label className={styles.label}>选择章节 *</label>
                <div className={styles.batchToolbar}>
                  <button
                    type="button"
                    className={styles.secondaryBtn}
                    onClick={handleSelectAllChapters}
                    disabled={volumesLoading || isGenerating || chaptersInVolume.length === 0}
                  >
                    {allChaptersSelected ? '取消全选' : '全选章节'}
                  </button>
                  <span className={styles.batchStats}>
                    已选 {selectedChapterIds.length}/{chaptersInVolume.length}
                    {chaptersInVolume.length > 0 && `，已有 ${notesReadyCount} 章生成 AI 笔记`}
                  </span>
                </div>
                {volumesLoading ? (
                  <div className={styles.statusMessage}>正在加载章节…</div>
                ) : chaptersInVolume.length > 0 ? (
                  <div className={styles.chapterList}>
                    {chaptersInVolume.map((chapter) => {
                      const checked = selectedChapterIds.includes(String(chapter.id));
                      const hasNotes = Boolean(chapter.has_ai_notes);
                      return (
                        <label key={chapter.id} className={styles.chapterItem}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => handleChapterToggle(chapter.id)}
                            className={styles.chapterCheckbox}
                          />
                          <div className={styles.chapterInfo}>
                            <span className={styles.chapterTitle}>
                              #{chapter.order_index ?? '-'} · {chapter.title}
                            </span>
                            <span
                              className={`${styles.chapterStatus} ${
                                hasNotes ? styles.badgeSuccess : styles.badgeMuted
                              }`}
                            >
                              {hasNotes ? '已生成 AI 笔记' : '未生成笔记'}
                            </span>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <div className={styles.statusMessage}>当前卷暂无章节。</div>
                )}
              </div>
            </>
          )}

          <div className={styles.fieldGroup}>
            <label className={styles.label}>模型名称</label>
            <input
              type="text"
              value={model}
              onChange={(event) => setModel(event.target.value)}
              className={styles.input}
              placeholder="例如：gpt-5-nano"
              disabled={isGenerating}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>额外生成说明（可选）</label>
            <textarea
              value={instructions}
              onChange={(event) => setInstructions(event.target.value)}
              className={styles.textarea}
              placeholder="补充对笔记风格、重点的说明，可留空"
              disabled={isGenerating}
            />
          </div>

          <div className={styles.buttonRow}>
            <button type="submit" className={styles.submitBtn} disabled={isGenerating}>
              {isGenerating
                ? selectedMode === MODE_UPLOAD
                  ? '生成中…'
                  : '批量生成中…'
                : selectedMode === MODE_UPLOAD
                ? '生成 AI 笔记'
                : '批量生成并写入数据库'}
            </button>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={handleReset}
              disabled={isGenerating}
            >
              重置
            </button>
            <a
              className={styles.downloadLink}
              href={docsUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              查看 API 文档
            </a>
          </div>
        </form>

        {error && <div className={styles.errorMessage}>{error}</div>}
      </section>

      {selectedMode === MODE_UPLOAD && result && (
        <section className={styles.resultCard}>
          <div className={styles.resultHeader}>
            <h3 className={styles.resultTitle}>生成完成</h3>
            <div className={styles.resultActions}>
              {result.saved_url && (
                <a
                  className={styles.downloadLink}
                  href={resolveStaticUrl(result.saved_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  下载笔记 JSON
                </a>
              )}
            </div>
          </div>

          <div className={styles.resultMeta}>
            <span>模型：{result.model}</span>
            <span>批次数：{result.batches_processed}/{result.total_batches}</span>
            <span>笔记条目：{Array.isArray(result.notes) ? result.notes.length : 0}</span>
            {result.saved_path && <span>保存路径：{result.saved_path}</span>}
            {result.is_partial && result.partial_reason && (
              <span>⚠ 部分生成：{result.partial_reason}</span>
            )}
          </div>

          <div className={styles.notesPreview}>
            {Array.isArray(result.notes) && result.notes.length > 0 ? (
              <div className={styles.notesList}>
                {result.notes.slice(0, 10).map((note, idx) => (
                  <div key={`preview-${note.sentence_id ?? idx}`} className={styles.noteItem}>
                    <p className={styles.noteSentence}>
                      {note.sentence_id ? `#${note.sentence_id} ` : ''}
                      {note.sentence_en || '—'}
                    </p>
                    {note.sentence_zh && (
                      <p className={styles.noteSentenceZh}>{note.sentence_zh}</p>
                    )}
                    {Array.isArray(note.key_phrases) && note.key_phrases.length > 0 && (
                      <ul className={styles.phraseList}>
                        {note.key_phrases.map((phrase, phraseIdx) => (
                          <li key={`${note.sentence_id ?? idx}-${phrase.phrase ?? phraseIdx}`}>
                            <strong>{phrase.phrase || '短语'}</strong>
                            {phrase.meaning ? `：${phrase.meaning}` : ''}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
                {result.notes.length > 10 && (
                  <p className={styles.description}>
                    已仅预览前 10 条，完整内容请下载 JSON 查看。
                  </p>
                )}
              </div>
            ) : (
              <div className={styles.statusMessage}>AI 未返回可用的笔记内容。</div>
            )}
          </div>
        </section>
      )}

      {selectedMode === MODE_BATCH && batchResult && (
        <section className={styles.resultCard}>
          <div className={styles.resultHeader}>
            <h3 className={styles.resultTitle}>批量生成完成</h3>
          </div>
          <div className={styles.resultMeta}>
            <span>请求章节：{batchResult.total_requested}</span>
            <span>完成：{batchResult.processed}</span>
            <span>成功：{batchResult.succeeded}</span>
            <span>失败：{batchResult.failed}</span>
            <span>模型：{batchResult.model}</span>
          </div>

          <div className={styles.batchResults}>
            {Array.isArray(batchResult.results) && batchResult.results.length > 0 ? (
              batchResult.results.map((item) => {
                const isSuccess = item.status === 'success';
                const badgeClass = isSuccess
                  ? item.is_partial
                    ? styles.badgeWarning
                    : styles.badgeSuccess
                  : styles.badgeDanger;
                const badgeLabel = isSuccess
                  ? item.is_partial
                    ? '部分成功'
                    : '成功'
                  : '失败';
                return (
                  <div key={item.chapter_id} className={styles.batchResultItem}>
                    <div className={styles.batchResultHeader}>
                      <span className={styles.batchResultTitle}>
                        #{item.chapter_id} · {item.title || '未命名章节'}
                      </span>
                      <span className={`${styles.badge} ${badgeClass}`}>{badgeLabel}</span>
                    </div>
                    <div className={styles.batchResultMeta}>
                      {typeof item.notes_count === 'number' && (
                        <span>笔记条目：{item.notes_count}</span>
                      )}
                      {item.saved_url && (
                        <a
                          href={resolveStaticUrl(item.saved_url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.batchResultLink}
                        >
                          下载 JSON
                        </a>
                      )}
                      {item.partial_reason && (
                        <span>⚠ {item.partial_reason}</span>
                      )}
                      {item.detail && !isSuccess && <span>原因：{item.detail}</span>}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className={styles.statusMessage}>未返回批量结果。</div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}

export default OpenAINotesTool;
